/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useContext, useEffect } from 'react';
import { useHistory } from 'react-router';
import { object, func, string } from 'prop-types';

// MUI
import {
    Card, CardContent, Grid,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

// Helpers
import { relationshipMapping, formatNameToProperCapitalization } from '../Common/Helpers/Helpers';

// Components
import OccasionsList from '../MobileRecipientBook/OccasionsList';
import RecipientAvatar from '../Common/Avatar/RecipientAvatar';
import { RecipientBookContext } from '../Common/Context/RecipientBookContext';
import { useDispatch } from 'react-redux';
import { trackEvent } from '../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { commonUseStyles } from '../../../../style/commonStyles';

const useStyles = makeStyles(({ palette }) => ({
    card: {
        backgroundColor: palette.common.white,
        '& .mbp-account-MuiCardContent-root:last-child': {
            padding: '16px 20px'
        },
        '& p': {
            marginBottom: '0 !important',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        }
    },
    cardContent: {
        height: 'auto',
        display: 'flex',
        alignItems: 'center',
        paddingBottom: '16px',
    },
    nameWrapper: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    name: {
        fontWeight: '700',
        lineHeight: '26px',
        margin: '0 10px',
        fontSize: '16px !important',
        cursor: 'pointer',
    },
    address: {
        fontSize: '16px !important',
        fontWeight: '400',
        marginRight: '5px',
        '& p': {
            marginBottom: '0px !important',
            fontSize: '16px',
            overflowWrap: 'break-word',
        },
    },
    occasionsListContainer: {
        marginTop: '10px !important',
        '& img, svg': {
            width: '17px',
            height: '17px',
        },
        '& .occasionName': {
            whiteSpace: 'nowrap',
            width: '117px',
            textOverflow: 'ellipsis',
            fontSize: '16px !important',
        },
    },
    editBtn: {
        color: `${palette.colorPrimaryAccountCtaButton} !important`,
        fontSize: '14px !important',
        cursor: 'pointer',
        fontWeight: '700 !important',
        textTransform: 'capitalize !important',
        backgroundColor: `${palette.common.white} !important`,
        border: 'none !important',
        borderBottom: `2px solid ${palette.colorPrimaryAccountCtaButton} !important`,
        padding: '0 0 2px 0px !important',
        minWidth: '0px !important',
        borderRadius: '0px !important',
    },
    recipientInfo: {
        '& p': {
            marginBottom: '0px !important',
        },
    },
}));

const DesktopRecipientCard = ({ recipient, globalClasses, setIsEditingRecipient, isLastSelfContactCardId, isLastVisibleContact, lazyLoadContacts }) => {
    const classes = useStyles();
    const customClasses = commonUseStyles();
    const { setCurrentRecipient } = useContext(RecipientBookContext);
    const dispatch = useDispatch();
    const history = useHistory();

    useEffect(() => {
        if (isLastVisibleContact) {
          const observer = new window.IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                lazyLoadContacts();
                observer.disconnect();
              }
            });
          });

          const lastElement = document.querySelector("#last-element");
          if (lastElement) {
            observer.observe(lastElement);
          }

          return () => observer.disconnect();
        }
      }, [isLastVisibleContact, lazyLoadContacts]);

    // render address on list only if address contains a valid country
    const isCountryUSAorCanada = (address) => ['US', 'USA', 'United States', 'CA', 'CAN', 'Canada'].includes(address.country);
    const defaultAddress = recipient?.addresses?.find((address) => address.defaultKey === '1' && isCountryUSAorCanada(address)) || recipient?.addresses?.find((address) => isCountryUSAorCanada(address)) || {};
    const recipientHasAddress = defaultAddress?.addressLineOne && defaultAddress?.city && defaultAddress?.state && defaultAddress?.zip;
    const recipientRelationship = relationshipMapping[recipient?.relationship] || null;

    const occasions = recipient?.occasions || []; // If a recipient has never added occasions, `recipient.occasions` is `undefined`.

    const getRecipNameOrCompanyName = () => {
        let name = '';
        if (!(recipient.rcpFirstName && recipient.rcpLastName) && recipient.rcpEmployerName) {
            name = formatNameToProperCapitalization(recipient.rcpEmployerName);
        } else if (recipient.rcpFirstName && recipient.rcpLastName) {
            name = formatNameToProperCapitalization(`${recipient.rcpFirstName} ${recipient.rcpLastName}`);
        }
        return name;
    };

    const editAddress = () => {
        dispatch(trackEvent({
            eventName: "address_book_interaction",
            interaction_type: "edit_contact_start",
            eventCategory: "Address Book",
            eventAction: "Interaction",
            eventLabel: "Edit Contact Start"
        }));

        setCurrentRecipient(recipient);
        setIsEditingRecipient(true);
        history.push(`/account/address-book/${recipient.rcpId}`);
    };

    return (
        <div id={isLastVisibleContact ? 'last-element' : ''} className={`${isLastSelfContactCardId ? globalClasses.lastRelationshipSelf : ''}`}>
            <Card className={classes.card}>
                <CardContent className={classes.cardContent}>
                    <Grid container item lg={12} md={12} direction='row' alignItems='center'>
                        <Grid container item lg={3} md={3} alignItems='center'>
                            <Grid item lg={2} md={3}>
                                <RecipientAvatar
                                    firstName={recipient?.rcpFirstName || recipient.rcpEmployerName}
                                    lastName={recipient?.rcpLastName}
                                    brandId='18B'
                                />
                            </Grid>
                            <Grid item lg={10} md={9} className={classes.nameWrapper} style={{ wordBreak: 'break-word', maxWidth: '250px' }}>
                                <p className={classes.name} onClick={editAddress}>{getRecipNameOrCompanyName()}</p>
                            </Grid>
                        </Grid>
                        <Grid item lg={1} md={2}>
                            <p className={customClasses.mediumFont}>{recipientRelationship}</p>
                        </Grid>
                        <Grid item lg={3} md={2}>
                            <div className={classes.address}>
                                {
                                    recipientHasAddress ? (
                                        <>
                                            <p className={customClasses.mediumFont}>{defaultAddress?.addressLineOne}</p>
                                            <p className={customClasses.mediumFont}>{defaultAddress?.city}, {defaultAddress?.state} {defaultAddress?.zip}</p>
                                        </>
                                    ) : <p className={customClasses.mediumFont}>No address on file</p>
                                }
                            </div>
                        </Grid>
                        <Grid item lg={4} md={3} container className={customClasses.gridAlign}>
                            <OccasionsList occasions={occasions} globalClasses={globalClasses} setIsEditingRecipient={setIsEditingRecipient} setCurrentRecipient={setCurrentRecipient} recipient={recipient} />
                        </Grid>
                        <Grid item lg={1} md={2} container direction="row" justifyContent="flex-end" alignItems="flex-start">
                            <button type="button" className={classes.editBtn} onClick={editAddress}>
                                Edit
                            </button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </div>
    );
};

DesktopRecipientCard.propTypes = {
    recipient: object.isRequired,
    globalClasses: object.isRequired,
    lazyLoadContacts: func.isRequired,
    setIsEditingRecipient: func.isRequired,
    isLastVisibleContact: string.isRequired,
};

export default DesktopRecipientCard;
